import { type Theme, useTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { REQUEST_STATUSES, TAB_STATUSES } from "@/constants";
import VisualBuilderLoadingSplash from "@/containers/LoadingComponent/VisualBuilderLoadingSplash";
import { getColorsPalette, useWidth, widthUtils } from "@/helpers";
import { useUrlParams } from "@/hooks";
import {
  getCurrentExperience,
  getVisualBuilderData,
  resetCurrentExperience,
  resetVisualBuilderData,
  setLoadingStatus,
  setSimpleValue,
  updateVisualBuilderData,
} from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import { currentOrganizationSelector } from "@/store/v2/currentOrganization/currentOrganizationSlice";
import { isPublicPage } from "@/utils/visualBuilder";

import {
  currentExperienceSelector,
  currentPropertySelector,
  loadingStatusSelector,
  visualBuilderDataSelector,
} from "../store/selectors";
import Drawer from "./components/Drawer/Drawer";
import ExperienceTabMenu from "./components/ExperienceTabMenu/ExperienceTabMenu";
import Fullscreen from "./components/Fullscreen/Fullscreen";
import NoContentItems from "./components/NoContentItems/NoContentItems";
import OpenGraphTags from "./components/OpenGraphTags";
import { useShareLinkTracking } from "./hooks/useShareLinkTracking";
import { useVisualBuilderData } from "./hooks/useVisualBuilderData";
import { useVisualBuilderListeners } from "./hooks/useVisualBuilderListeners";

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: "flex",
    flexGrow: 1,
    minHeight: "40px",
    height: "100%",
  },
  bodyContent: {
    width: "100%",
    height: "100%",
    backgroundColor: "#2c2c2c",
    overflow: "auto",
    transition: theme.transitions.create(["paddingTop"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  notFound: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    height: "100%",
  },
  loadingBackground: {
    backgroundColor: "rgb(44, 44, 44)",
    height: "100%",
  },
  noResult: {
    display: "flex",
    "justify-content": "center",
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  progressRoot: {
    width: "25%",
    height: 4,
    backgroundColor: "grey",
  },
  barPrimary: {
    backgroundColor: "white",
  },
  goBack: {
    backgroundColor: "#fff",
    padding: "15px 24px",
    borderRadius: "24px",
    border: "1px solid black",
  },
  splashPageLogo: {
    maxWidth: "200px",
    maxHeight: "200px",
  },
}));

interface VisualBuilderProps {
  readonly title?: string;
  readonly publicPage?: boolean;
}

export default function VisualBuilder(props: VisualBuilderProps) {
  const { title = "", publicPage = false } = props;

  const navigate = useNavigate();
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useThunkDispatch();

  const visualBuilderRef = useRef<HTMLDivElement>(null);

  useShareLinkTracking();
  useVisualBuilderListeners();
  useVisualBuilderData();

  const width = useWidth();
  const [searchParams] = useSearchParams();
  const shareId = searchParams.get("shareId");
  const params = useUrlParams();
  const { propertyId, experienceId, tabStatus } = params;

  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { mediaItems = [] } = visualBuilderData;

  const noMediaResults = visualBuilderData.id && !mediaItems.length;

  const { property } = useSelector(currentPropertySelector);
  const { experience, status: experienceReqStatus } = useSelector(currentExperienceSelector);
  const { isLoading } = useSelector(loadingStatusSelector);

  const { splashPageBackgroundColor, splashPageLogo } = property;

  const primaryColorSettings = property.themeColor || "#71B6BC";

  const [primaryColor, secondaryColor] = getColorsPalette(primaryColorSettings);

  const { isWidthDown } = widthUtils(theme);
  const belowSmallWidth = isWidthDown("sm", width);

  const experienceDoesNotExist = experienceId && experienceReqStatus === REQUEST_STATUSES.FAILED;

  useEffect(() => {
    if (publicPage) {
      dispatch(setSimpleValue("public-page", true));
    }
  }, [publicPage]);

  useEffect(() => {
    let loading = false;

    const collectOrCustomize = tabStatus === TAB_STATUSES.COLLECT || tabStatus === TAB_STATUSES.CUSTOMIZE;

    if (collectOrCustomize && !experience.id) {
      loading = true;
    }

    if (!visualBuilderData.id || !property.id) {
      loading = true;
    }

    if (!visualBuilderData.initialized) {
      loading = true;
    }

    if (experienceDoesNotExist) {
      loading = false;
    }

    dispatch(
      setLoadingStatus({
        dataLoading: loading,
        isPublicView: !!property.splashPageLogo && isPublicPage(tabStatus ?? ""),
      }),
    );
  }, [
    visualBuilderData.id,
    visualBuilderData.initialized,
    property.id,
    experience.id,
    property.id,
    tabStatus,
    experienceDoesNotExist,
  ]);

  const { data: organization } = useSelector(currentOrganizationSelector);
  const organizationId = organization?.id;
  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const tabPrefix = isOrganizationsSection ? `/organizations/${organizationId}` : "";
  const startingAssetId = experienceId ? experience?.startingAssetId : property?.startingAssetId;

  useEffect(() => {
    // Redirect to all assets share page if experience does not exist
    // and user is on a public page, eg: /properties/1/experiences/1, /properties/1/experiences/1?shareId=1
    if (publicPage && experienceDoesNotExist) {
      if (shareId) {
        dispatch(resetVisualBuilderData(params));
        dispatch(resetCurrentExperience());

        navigate(`${tabPrefix}/properties/${propertyId}/experiences/${experienceId}`, { replace: true });

        dispatch(getVisualBuilderData(params));
        dispatch(getCurrentExperience(params));
      } else {
        navigate(`${tabPrefix}/properties/${propertyId}/all-assets-share`, { replace: true });
      }
    }
  }, [propertyId, experienceId, publicPage, experienceDoesNotExist, params, shareId]);

  useEffect(() => {
    const timer = setTimeout(() => dispatch(setLoadingStatus({ splashTimeout: true })), 3000);
    return () => clearTimeout(timer);
  }, [propertyId]);

  const hasInitializedStartingPoint = useRef(false);

  useEffect(() => {
    if (!publicPage || hasInitializedStartingPoint.current) return;
    if (!startingAssetId || !mediaItems.length) return;

    const initialMediaItem = mediaItems.find((item) => item.id === startingAssetId);
    if (!initialMediaItem) return;

    dispatch(
      updateVisualBuilderData(
        params,
        {
          currentMediaItemId: initialMediaItem.id,
          assetCategoryFilterId: initialMediaItem.assetCategoryId,
          typeFilterId: initialMediaItem.contentTypeId,
        },
        {},
        true,
      ),
    );
    hasInitializedStartingPoint.current = true;
  }, [publicPage, mediaItems, startingAssetId]);

  const homeBaseOrCollect = tabStatus === TAB_STATUSES.HOMEBASE || tabStatus === TAB_STATUSES.COLLECT;

  return (
    <ThemeProvider
      theme={() => ({
        ...theme,
        primaryColor,
        secondaryColor,
      })}
    >
      {property.name && <OpenGraphTags image={property.previewImageUrl} title={property.name}></OpenGraphTags>}
      <VisualBuilderLoadingSplash
        splashPageBackgroundColor={splashPageBackgroundColor}
        splashPageLogo={splashPageLogo}
        isLoading={isLoading}
      />
      {!isLoading && experienceDoesNotExist ? (
        <div className={classes.notFound}>
          <h1>uh oh!</h1>
          <p>Experience has been deleted or does not exist.</p>
        </div>
      ) : (
        <>
          <div
            className={classes.root}
            data-testid="visual-builder"
            style={{ visibility: isLoading ? "hidden" : "visible" }}
          >
            <ExperienceTabMenu label={title} belowSmallWidth={belowSmallWidth} />
            <Drawer propertyId={propertyId!} visualBuilderRef={visualBuilderRef} startingAssetId={startingAssetId} />
            <div ref={visualBuilderRef} tabIndex={-1} id="visual-builder-body-content" className={classes.bodyContent}>
              <Fullscreen />
            </div>
          </div>
          {homeBaseOrCollect && noMediaResults ? <NoContentItems tabStatus={tabStatus} propertyId={propertyId!} /> : ""}
        </>
      )}
    </ThemeProvider>
  );
}
